/**
 * Ability area + live-scaled range/dash helpers.
 * liveAbilityCast sets caster.liveWorldScale (sandbox xz).
 */

export function abilityWorldScale(caster) {
  const s = caster?.liveWorldScale;
  return typeof s === 'number' && s > 0 ? s : 1;
}

export function scaleLolDistance(lolUnits, caster) {
  if (!Number.isFinite(lolUnits)) return 0;
  return lolUnits * abilityWorldScale(caster);
}

export function isInAbilityCircle(entity, originX, originY, lolRadius, caster) {
  if (!entity || !Number.isFinite(lolRadius)) return false;
  const max = scaleLolDistance(lolRadius, caster);
  return Math.hypot(entity.x - originX, entity.y - originY) <= max;
}

export function isWithinAbilityRange(a, b, lolRange, caster) {
  if (!a || !b || !Number.isFinite(lolRange)) return false;
  return Math.hypot(a.x - b.x, a.y - b.y) <= scaleLolDistance(lolRange, caster);
}

export function isInAbilityLine(entity, originX, originY, angle, lolRange, lolHalfWidth, caster) {
  if (!entity || !Number.isFinite(lolRange) || !Number.isFinite(lolHalfWidth)) return false;
  const range = scaleLolDistance(lolRange, caster);
  const halfW = scaleLolDistance(lolHalfWidth, caster);
  const dx = entity.x - originX;
  const dy = entity.y - originY;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-6 || dist > range) return false;
  const along = dx * Math.cos(angle) + dy * Math.sin(angle);
  if (along < 0 || along > range) return false;
  const cross = Math.abs(-dx * Math.sin(angle) + dy * Math.cos(angle));
  return cross <= halfW;
}

export function isInAbilityCone(entity, originX, originY, angle, lolRange, halfAngleRad, caster) {
  if (!entity || !Number.isFinite(lolRange) || !Number.isFinite(halfAngleRad)) return false;
  const range = scaleLolDistance(lolRange, caster);
  const dx = entity.x - originX;
  const dy = entity.y - originY;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-6 || dist > range) return false;
  const a = Math.atan2(dy, dx);
  let diff = Math.abs(a - angle);
  if (diff > Math.PI) diff = 2 * Math.PI - diff;
  return diff <= halfAngleRad;
}

export function isInAbilityRing(entity, originX, originY, lolInner, lolOuter, caster) {
  if (!entity || !Number.isFinite(lolInner) || !Number.isFinite(lolOuter)) return false;
  const inner = scaleLolDistance(lolInner, caster);
  const outer = scaleLolDistance(lolOuter, caster);
  const dist = Math.hypot(entity.x - originX, entity.y - originY);
  return dist >= inner && dist <= outer;
}

/** Clamp a ground-click to the caster's kit range (Brand W / Lux E / …). */
export function clampGroundCast(caster, x, y, lolMaxRange) {
  if (!caster || !Number.isFinite(lolMaxRange)) return { x, y };
  return clampPointToward(caster.x, caster.y, x, y, lolMaxRange, caster);
}

/** Clamp point within LoL max range (scaled for live). */
export function clampPointToward(ox, oy, tx, ty, lolMaxRange, caster) {
  const maxRange = scaleLolDistance(lolMaxRange, caster);
  const dx = tx - ox;
  const dy = ty - oy;
  const dist = Math.hypot(dx, dy) || 1;
  if (dist <= maxRange) return { x: tx, y: ty };
  const t = maxRange / dist;
  return { x: ox + dx * t, y: oy + dy * t };
}

/** Move caster toward point, capped by LoL dash range (scaled for live). */
export function blinkToward(caster, tx, ty, lolMaxRange) {
  if (!caster || !Number.isFinite(lolMaxRange)) return false;
  const next = clampPointToward(caster.x, caster.y, tx, ty, lolMaxRange, caster);
  caster.x = next.x;
  caster.y = next.y;
  if (Number.isFinite(caster.z)) caster.z = caster.y;
  return true;
}
